import { request } from '@/utils/http'
import { UserType } from '@/enums/auth'
import { useAuthStore } from '@/stores/auth'

// ===================== 类型定义 =====================

// 企业联系人请求结构
export interface EnterpriseContactRequest {
  id?: string
  enterpriseId?: string
  action?: string
  contactName: string
  contactPosition?: string
  contactPhone: string
  contactEmail?: string
  ifReconciliation?: boolean
  ifDefault?: boolean
}

// 企业联系人返回结构
export interface EnterpriseContactVO {
  externalSource?: string
  id: string
  enterpriseId?: string | null
  contactName: string
  contactPosition?: string
  contactPhone: string
  contactEmail?: string
  ifDefault?: boolean
  ifReconciliation?: boolean
  responsibilityEnable?: boolean
  containerLoadTypes?: string[]
  loadingPortIds?: string[]
  routeIds?: string[]
  carrierIds?: string[]
  wechatWorkRpaGroupName?: string
}

// 企业开票信息请求结构
export interface EnterpriseInvoiceInfoRequest {
  invoiceCompanyName: string
  invoiceTaxNo: string
  invoiceAddress?: string
  invoicePhone?: string
  invoiceBank?: string
  invoiceBankAccount?: string
}

// 企业开票信息返回结构
export interface EnterpriseInvoiceInfoVO {
  externalSource?: string
  upstreamRemoved?: boolean
  id?: string
  invoiceCompanyName: string
  invoiceTaxNo: string
  invoiceAddress?: string
  invoicePhone?: string
  invoiceBank?: string
  invoiceBankAccount?: string
}

// 银行账户请求结构
export interface EnterpriseBankAccountRequest {
  bankName: string
  bankAccount: string
  accountName: string
  swiftCode?: string
  bankAddress?: string
}

// 银行账户返回结构
export interface EnterpriseBankAccountVO {
  externalSource?: string
  id?: string
  bankName: string
  bankAccount: string
  accountName: string
  swiftCode?: string
  bankAddress?: string
}

// 企业对接信息
export interface EnterpriseDedicatedRelRequest {
  userId: string
  dedicatedType: number
}
export interface EnterpriseDedicatedRelVO {
  userId: string
  dedicatedType: number
  username: string
  phone: string
  email: string
}

// 企业运价等级配置
export interface EnterpriseFreightLevelConfigRequest {
  businessType: string
  freightLevel: number
}
export interface EnterpriseFreightLevelConfigVO {
  id?: string
  enterpriseId?: string
  businessType: string
  freightLevel: number
}

// 企业用户关联操作
export interface EnterpriseUserRelActionRequest {
  enterpriseId: string
  userId: string
  roleType: number
  action: string
}

// 企业用户关联返回
export interface EnterpriseUserRelVO {
  id: string
  tenantId: string
  enterpriseId: string
  roleType: number
  userId: string
  username: string
  realName: string
  phone: string
  email: string
  avatar?: string
  status: number
  loginTime?: string
  dateCreated: string
}

// 企业详情返回结构
export interface EnterpriseVO {
  id: string
  enterpriseCode?: string
  status: number
  enterpriseName: string
  enterpriseNameEn: string
  businessLicense: string
  businessLicenseFile?: string
  countryCode?: string
  provinceCode?: string
  cityCode?: string
  districtCode?: string
  streetCode?: string
  addressDetail: string
  remark?: string
  enterpriseType: number
  contacts?: EnterpriseContactVO[]
  userRelList?: EnterpriseUserRelVO[]
  invoiceInfo?: EnterpriseInvoiceInfoVO
  cnyBankAccount?: EnterpriseBankAccountVO
  usdBankAccount?: EnterpriseBankAccountVO
  reconciliationContact?: EnterpriseContactVO
  freightLevelConfigList?: EnterpriseFreightLevelConfigVO[]
  dedicatedRelList?: EnterpriseDedicatedRelVO[]
  roleType?: number
  dateCreated: string,
  auditReason: string,
  businessLicenseFileInfo?: BusinessLicenseFileInfoVO
}

// 企业认证/更新请求参数（租户端）
export interface TenantEnterpriseSubmitRequest {
  enterpriseName: string
  enterpriseNameEn: string
  enterpriseCode?: string
  businessLicense: string
  businessLicenseFile?: string
  countryCode?: string
  provinceCode?: string
  cityCode?: string
  districtCode?: string
  streetCode?: string
  addressDetail: string
  remark?: string
  contacts: EnterpriseContactRequest[]
  userRelAction?: EnterpriseUserRelActionRequest[]
  invoiceInfo: EnterpriseInvoiceInfoRequest
  cnyBankAccount: EnterpriseBankAccountRequest
  usdBankAccount?: EnterpriseBankAccountRequest
  dedicatedRelList?: EnterpriseDedicatedRelRequest[]
  freightLevelConfigList?: EnterpriseFreightLevelConfigRequest[]
}

// 企业认证/更新请求参数（客户端）
export interface ClientEnterpriseSubmitRequest {
  enterpriseName: string
  enterpriseNameEn: string
  enterpriseCode?: string
  businessLicense: string
  businessLicenseFile?: string
  countryCode?: string
  provinceCode?: string
  cityCode?: string
  districtCode?: string
  streetCode?: string
  addressDetail: string
  remark?: string
  contacts: EnterpriseContactRequest[]
  invoiceInfo: EnterpriseInvoiceInfoRequest
  cnyBankAccount: EnterpriseBankAccountRequest
  usdBankAccount?: EnterpriseBankAccountRequest
}

// 加入企业请求参数
export interface JoinEnterpriseRequest {
  hasInviteCode: boolean
  inviteCode?: string
  enterpriseName?: string
  applyMessage?: string
}

// 邀请员工请求参数
export interface InviteEmployeeRequest {
  email: string
  inviteCode: string
  inviteLink: string
}

// 分页查询基础参数
export interface PageRequest {
  pageNum: number
  pageSize: number
  sort?: string
  paging?: boolean
  conditions?: any[]
  sorts?: any[]
}

// 邀请记录分页请求
export interface InvitationPageRequest extends PageRequest {}

// 作废邀请链接请求参数
export interface InvalidateInvitationRequest {
  invitationId: string
}

// 申请分页请求
export interface ApplicationPageRequest extends PageRequest {}

// 审核申请请求参数
export interface AuditApplicationRequest {
  applicationId: string
  auditResult: boolean
  auditReason: string
}

// 邀请链接返回结构
export interface InviteLinkVO {
  inviteCode: string
  inviteLink: string
}

// 邀请记录单项
export interface EnterpriseInvitationPageVO {
  enterpriseId: string
  inviterEmail: string
  invitationCode: string
  invitationLink: string
  expiryTime: string
  status: number
  joinTime: string | null
  dateCreated: string
}

// 邀请记录分页返回结构
export interface Page_EnterpriseInvitationPageVO {
  pageNum: number
  pageSize: number
  total: number
  list: EnterpriseInvitationPageVO[]
}

// 申请记录单项
export interface EnterpriseUserApplicationPageVO {
  id: string
  userId: string
  userName: string
  realName: string
  phone: string
  email: string
  applicationReason: string
  status: number
  auditReason?: string
  dateCreated: string
}

// 申请记录分页返回结构
export interface Page_EnterpriseUserApplicationPageVO {
  pageNum: number
  pageSize: number
  total: number
  list: EnterpriseUserApplicationPageVO[]
}

// 营业执照信息DTO
export interface BusinessLicenseFileInfoVO {
  fileName: string
  fileType: string
  contentType?: string
  inputStream?: string
  content?: string
}

// ===================== 租户端/客户端企业中心接口 =====================

// 获取当前用户类型
function getCurrentUserType() {
  const authStore = useAuthStore()
  return authStore.user.userType
}

// 内部分流工具
function getApiPrefix() {
  const userType = getCurrentUserType()
  return userType === UserType.TENANT ? '/enterprise/tenant/enterprise' : '/enterprise/client/enterprise'
}

/**
 * 获取企业详情
 */
export const getEnterpriseDetail = (): Promise<EnterpriseVO> => {
  return request.post<EnterpriseVO>(`${getApiPrefix()}/detail`)
}

/**
 * 企业认证
 */
export const submitEnterprise = (
  params: TenantEnterpriseSubmitRequest | ClientEnterpriseSubmitRequest
): Promise<EnterpriseVO> => {
  return request.post<EnterpriseVO>(`${getApiPrefix()}/submit`, params)
}

/**
 * 企业修改
 */
export const updateEnterprise = (
  params: TenantEnterpriseSubmitRequest | ClientEnterpriseSubmitRequest
): Promise<EnterpriseVO> => {
  return request.post<EnterpriseVO>(`${getApiPrefix()}/update`, params)
}

/**
 * 加入企业
 */
export const joinEnterprise = (
  params: JoinEnterpriseRequest
): Promise<void> => {
  return request.post<void>(`${getApiPrefix()}/join`, params)
}

/**
 * 获取邀请链接
 */
export const getInviteLink = (): Promise<InviteLinkVO> => {
  return request.post<InviteLinkVO>(`${getApiPrefix()}/inviteLink`)
}

/**
 * 邀请员工
 */
export const inviteUser = (
  params: InviteEmployeeRequest
): Promise<void> => {
  return request.post<void>(`${getApiPrefix()}/inviteUser`, params)
}

/**
 * 分页查询邀请记录
 */
export const getInvitationPage = (
  params: InvitationPageRequest
): Promise<Page_EnterpriseInvitationPageVO> => {
  return request.post<Page_EnterpriseInvitationPageVO>(`${getApiPrefix()}/invitation/page`, params)
}

/**
 * 作废邀请链接
 */
export const invalidateInvitation = (
  params: InvalidateInvitationRequest
): Promise<void> => {
  return request.post<void>(`${getApiPrefix()}/invitation/invalidate`, params)
}

/**
 * 分页查询员工加入企业申请
 */
export const getApplicationPage = (
  params: ApplicationPageRequest
): Promise<Page_EnterpriseUserApplicationPageVO> => {
  return request.post<Page_EnterpriseUserApplicationPageVO>(`${getApiPrefix()}/application/page`, params)
}

/**
 * 审核员工加入企业申请
 */
export const auditApplication = (
  params: AuditApplicationRequest
): Promise<void> => {
  return request.post<void>(`${getApiPrefix()}/application/audit`, params)
}
